/* eslint-disable indent */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';

import { Button } from 'primereact/button';
import { COUNCIL_STATUS, COUNCIL_TYPE_CODE, TIMEZONE_VN } from 'constant';
import { formatFileName, getMaxDate } from 'utils/func';
import { TextareaInput } from 'components/FormControl';
import RadioInput from 'components/FormControl/RadioInput';

const mapTooltips = {
  REQUEST_EXPLANATION_PREQUALIFICATION: 'supportOwnerExplanationPrequalification',
  REQUEST_EXPLANATION_SPECIALIZATION: 'supportOwnerExplanationSpecialization',
  REQUEST_EXPLANATION_FINANCE: 'supportOwnerExplanationFinance',
};

const viewCompareCouncilTypeCodes = [
  COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL,
  COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL,
  COUNCIL_TYPE_CODE.FINANCE_COUNCIL,
];

export default function OwnerExplanation({
  control,
  errors,
  isDirty,
  dirtyFields,
  handleSubmit,
  checkValidation,
  handleDownloadFile,
  explanationRequest,
  correspondingVersion,
  council,
  program,
  isReadOnly,
}) {
  const { t } = useTranslation();

  const formatDeadline = () => {
    let deadline = new Date(explanationRequest?.deadline);
    if (council?.councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      deadline = getMaxDate(deadline, program?.staffPrequalificationDeadline);
    }
    return deadline?.getTime()
      ? new Date(deadline).toLocaleDateString('en-GB', {
          timeZone: TIMEZONE_VN,
        })
      : t('projectProposal.requestExplanation.noDeadline');
  };

  const formatFeedbackStatus = (item) => {
    if (
      new Date(item?.createdAt)?.getTime() <
      new Date(explanationRequest?.explanationTime)?.getTime()
    ) {
      return (
        <Tag
          value={t('projectProposal.requestExplanation.feedbackStatuses.ownerHasAddedExplanation')}
          severity="success"
          rounded
        />
      );
    }

    return (
      <Tag
        value={
          explanationRequest?.isOpened
            ? t('projectProposal.requestExplanation.feedbackStatuses.ownerNotAddExplanationYet')
            : t('projectProposal.requestExplanation.feedbackStatuses.notRequestExplanation')
        }
        severity={explanationRequest?.isOpened ? 'warning' : 'success'}
        rounded
      />
    );
  };

  return (
    <div>
      {explanationRequest && (
        <div className="mt-2">
          <div className="flex align-items-center justify-content-between mb-1">
            <div className="flex align-items-center">
              {(explanationRequest?.isOpened || explanationRequest?.ownerComment) && (
                <h4>{t('projectProposal.requestExplanation.ownerExplanation')}</h4>
              )}
              {explanationRequest?.isOpened && !explanationRequest?.ownerComment && (
                <Message
                  severity="error"
                  text={t('projectProposal.requestExplanation.ownerNotAddExplanationYet')}
                  className="w-max font-semibold ml-2 support-owner-explanation"
                />
              )}
              {!explanationRequest?.isOpened && explanationRequest?.ownerComment && (
                <Message
                  severity="success"
                  text={t('projectProposal.requestExplanation.updatedAfterFeedback')}
                  className="w-max font-semibold ml-2"
                />
              )}
              {explanationRequest?.isOpened && explanationRequest?.ownerComment && (
                <Message
                  severity="warn"
                  text={t('projectProposal.requestExplanation.notUpdatedAfterFeedback')}
                  className="w-max font-semibold ml-2 support-owner-explanation"
                />
              )}
              <Tooltip
                target=".support-owner-explanation"
                content={t(
                  `projectProposal.requestExplanation.${mapTooltips?.[explanationRequest?.type]}`
                )}
                position="right"
                className="w-19rem"
              />
            </div>
            {explanationRequest?.explanationTime && (
              <div className="w-auto flex justify-content-between text-lg">
                <span className="mr-2 font-semibold">
                  {t('projectProposal.requestExplanation.updatedAt')}:
                </span>
                <span>
                  {new Date(explanationRequest?.explanationTime)?.toLocaleString('en-GB', {
                    timeZone: TIMEZONE_VN,
                  })}
                </span>
              </div>
            )}
          </div>

          {(explanationRequest?.isOpened || explanationRequest?.explanationTime) && (
            <div className="ml-5 text-red-500 text-lg font-italic mb-2">
              {t('projectProposal.requestExplanation.deadline')}: {formatDeadline()}
            </div>
          )}

          {/*
            Trường hợp:
            ( Hội đồng Sơ tuyển/Chuyên môn/Tài chính
            VÀ  "yêu cầu giải trình chỉnh sửa đang mở"
            VÀ "chủ nhiệm chưa giải trình lần nào"
            VÀ "đề tài đã được Hoàn thành chỉnh sửa"
            )
            => Hiển thị link đến màn hình so sánh phiên bản
        	*/}
          {viewCompareCouncilTypeCodes.includes(council?.councilType?.code) &&
            explanationRequest?.isOpened &&
            !explanationRequest?.ownerComment &&
            correspondingVersion?.isEditable === 0 && (
              <div className="ml-5 text-lg p-fluid">
                <Link to={`compare-version?version=${correspondingVersion?.version}`}>
                  <span className="block mb-2 underline">
                    {t('projectProposal.requestExplanation.viewVersionsComparison')}
                  </span>
                </Link>
              </div>
            )}

          {explanationRequest && (
            <div className="ml-5 text-lg p-fluid">
              {explanationRequest?.ownerComment && (
                <>
                  {viewCompareCouncilTypeCodes.includes(council?.councilType?.code) && (
                    <Link to={`compare-version?version=${correspondingVersion?.version}`}>
                      <span className="block mb-2 underline">
                        {t('projectProposal.requestExplanation.viewVersionsComparison')}
                      </span>
                    </Link>
                  )}

                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.requestExplanation.comment')}:
                    </span>
                    <p className="font-italic my-2 text-justify" style={{ whiteSpace: 'pre-wrap' }}>
                      {explanationRequest?.ownerComment}
                    </p>
                  </div>

                  <div className="mb-4">
                    <span className="mr-2 font-semibold">
                      {t('projectProposal.requestExplanation.commentFile')}:
                    </span>
                    {explanationRequest?.ownerCommentFileUrls?.length > 0 ? (
                      <ul className="list-disc text-lg">
                        {explanationRequest?.ownerCommentFileUrls?.map((fileUrl, index) => (
                          <li className="mb-2" key={fileUrl?.id ?? index}>
                            <span
                              className="p-link text-primary underline"
                              onClick={() => handleDownloadFile(fileUrl?.name, council?.id)}
                            >
                              {formatFileName(fileUrl?.name)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      t('projectProposal.requestExplanation.noCommentFile')
                    )}
                  </div>
                </>
              )}

              {(explanationRequest?.isOpened || explanationRequest?.ownerComment) && (
                <form onSubmit={handleSubmit}>
                  <div className="grid mt-2">
                    <label htmlFor="feedback" className="col-12 font-semibold">
                      {t('projectProposal.requestExplanation.specialistFeedback')}:
                    </label>
                    {!isReadOnly && (
                      <div className="col-12">
                        <TextareaInput
                          name="feedback"
                          control={control}
                          errors={errors}
                          row={5}
                          disabled={
                            council?.projectProposalCouncils?.[0]?.councilStatus !==
                            COUNCIL_STATUS.QUALIFIED
                          }
                        />
                      </div>
                    )}

                    {explanationRequest?.feedbacks?.length > 0 && (
                      <div className="form-table col-12">
                        <table style={{ borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'center' }}>
                                {t('projectProposal.expenditure.expenditure.fields.order')}
                              </th>
                              <th
                                style={{
                                  width: 'auto',
                                  minWidth: '24rem',
                                  textAlign: 'center',
                                }}
                              >
                                {t('projectProposal.requestExplanation.feedback')}
                              </th>
                              <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                                {t('projectProposal.requestExplanation.time')}
                              </th>
                              <th style={{ minWidth: '6rem', textAlign: 'center' }}>
                                {t('projectProposal.requestExplanation.status')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {explanationRequest?.feedbacks?.map((item, index) => (
                              <tr key={item?.id ?? index}>
                                <td className="text-center">{index + 1}</td>
                                <td style={{ whiteSpace: 'pre-wrap' }}>{item?.content}</td>
                                <td className="text-center">
                                  {/* eslint-disable indent */}
                                  {item?.createdAt
                                    ? new Date(item?.createdAt)?.toLocaleString('en-GB', {
                                        timeZone: TIMEZONE_VN,
                                      })
                                    : ''}
                                </td>
                                <td className="text-center">{formatFeedbackStatus(item)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {!isReadOnly && (
                      <>
                        <label htmlFor="isOpened" className="col-12 font-semibold pb-0">
                          {t(
                            'projectProposal.requestExplanation.projectProposalRequireUpdateExplanation'
                          )}
                        </label>
                        <div className="col-12">
                          <RadioInput
                            name="isOpened"
                            control={control}
                            errors={errors}
                            options={[
                              { value: true, name: t('formLayout.yes') },
                              { value: false, name: t('formLayout.no') },
                            ]}
                            inputField="value"
                            disabled={
                              council?.projectProposalCouncils?.[0]?.councilStatus !==
                              COUNCIL_STATUS.QUALIFIED
                            }
                          />
                        </div>
                      </>
                    )}

                    {!isReadOnly &&
                      viewCompareCouncilTypeCodes.includes(council?.councilType?.code) && (
                        <>
                          <label htmlFor="isEditAllowed" className="col-12 font-semibold py-0">
                            {t('projectProposal.requestExplanation.allowOwnerEditProjectProposal')}
                          </label>
                          <div className="col-12">
                            <RadioInput
                              name="isEditAllowed"
                              control={control}
                              errors={errors}
                              options={[
                                { value: true, name: t('formLayout.yes') },
                                { value: false, name: t('formLayout.no') },
                              ]}
                              inputField="value"
                              defaultValue={false}
                              disabled={
                                council?.projectProposalCouncils?.[0]?.councilStatus !==
                                COUNCIL_STATUS.QUALIFIED
                              }
                            />
                          </div>
                        </>
                      )}
                  </div>
                  {!isReadOnly && (
                    <div className="flex justify-content-end">
                      <Button
                        label={t('projectProposal.requestExplanation.feedback')}
                        severity="success"
                        type="submit"
                        onClick={checkValidation}
                        disabled={
                          !(isDirty && Object.keys(dirtyFields).length) ||
                          council?.projectProposalCouncils?.[0]?.councilStatus !==
                            COUNCIL_STATUS.QUALIFIED
                        }
                        className="w-8rem"
                      />
                    </div>
                  )}
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

OwnerExplanation.propTypes = {
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  isDirty: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  dirtyFields: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  checkValidation: PropTypes.func.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  explanationRequest: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  correspondingVersion: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  council: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  program: PropTypes.object,
  isReadOnly: PropTypes.bool,
};

OwnerExplanation.defaultProps = {
  isDirty: false,
  dirtyFields: {},
  explanationRequest: null,
  correspondingVersion: null,
  council: null,
  program: null,
  isReadOnly: false,
};
